<script setup lang="ts">
import { nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import SpinWheel, { type WheelSegment } from './SpinWheel.vue'
import SpinWheelService, { type SpinWheelResult } from '@/services/SpinWheelService'

const props = defineProps<{
  open: boolean
  segments: WheelSegment[]
  moduleId: string
  loggedIn: boolean
  accountEmail?: string
  accountFirstName?: string
  accountLastName?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'request-login'): void
}>()

// Server returns the label of the prize it drew; this is where the wheel stops.
const NO_LUCK_LABEL = 'Sorry! No luck'
const RESULT_DELAY_MS = 600

const ERRORS: Record<string, string> = {
  error_login: 'Please sign in to spin the wheel.',
  error_email_invalid: 'Please enter a valid email address.',
  error_already_played: 'You have already spun the wheel in this session.',
  error_email_exist: 'This email address has already spun the wheel.',
  error_busy: 'The wheel is busy right now. Please try again in a moment.',
}
const FALLBACK_ERROR = 'Something went wrong. Please try again.'

type Stage = 'form' | 'checking' | 'spinning' | 'result' | 'error'

const dialog = ref<HTMLElement | null>(null)
const wheel = ref<InstanceType<typeof SpinWheel> | null>(null)
const stage = ref<Stage>('form')
const form = reactive({ email: '', firstname: '', lastname: '' })
const fieldErrors = reactive({ email: '', firstname: '', lastname: '' })
const formError = ref('')
const guestPrompt = ref('')
const outcome = ref<SpinWheelResult | null>(null)
let resultTimer = 0
let returnFocusTo: HTMLElement | null = null

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

watch(() => props.loggedIn, () => { guestPrompt.value = '' })

// Signed in: the email is the account's (read only) and the names start from the account.
watch(
  () => [props.open, props.loggedIn, props.accountEmail] as const,
  ([isOpen, signedIn]) => {
    if (!isOpen || !signedIn) return
    form.email = props.accountEmail || ''
    if (!form.firstname) form.firstname = props.accountFirstName || ''
    if (!form.lastname) form.lastname = props.accountLastName || ''
  },
  { immediate: true }
)

function validate(): boolean {
  fieldErrors.email = EMAIL_PATTERN.test(form.email.trim()) ? '' : 'Please enter a valid email address.'
  fieldErrors.firstname = form.firstname.trim() ? '' : 'Please enter your first name.'
  fieldErrors.lastname = form.lastname.trim() ? '' : 'Please enter your last name.'
  return !fieldErrors.email && !fieldErrors.firstname && !fieldErrors.lastname
}

function errorFor(response: SpinWheelResult): string {
  const key = (Object.keys(ERRORS) as (keyof SpinWheelResult)[]).find((k) => response[k])
  return key ? ERRORS[key] : FALLBACK_ERROR
}

function indexForLabel(label?: string): number {
  const index = props.segments.findIndex((segment) => segment.label === label)
  return index === -1 ? props.segments.findIndex((s) => s.label === NO_LUCK_LABEL) : index
}

async function onGo() {
  formError.value = ''
  if (!props.loggedIn) {
    guestPrompt.value = 'Please log in or create an account before you can spin.'
    return
  }
  if (stage.value !== 'form' || !validate()) return

  stage.value = 'checking'
  let response: SpinWheelResult
  try {
    response = await SpinWheelService.spin({
      module_id: props.moduleId,
      email: form.email.trim(),
      firstname: form.firstname.trim(),
      lastname: form.lastname.trim(),
    })
  } catch {
    stage.value = 'error'
    formError.value = FALLBACK_ERROR
    return
  }

  if (!response?.success) {
    stage.value = 'error'
    formError.value = errorFor(response ?? ({} as SpinWheelResult))
    return
  }

  outcome.value = response
  stage.value = 'spinning'
  wheel.value?.spinTo(indexForLabel(response.label))
}

function onWheelDone() {
  // Hold the wheel for a moment so the landing is seen before the reward card appears.
  // The prize email is queued by the server during save(), so nothing is sent from here.
  resultTimer = window.setTimeout(() => {
    stage.value = 'result'
  }, RESULT_DELAY_MS)
}

function requestClose() {
  // The wheel cannot be dismissed mid-spin; the prize is already drawn.
  if (stage.value === 'spinning' || stage.value === 'checking') return
  emit('close')
}

function onDone() {
  if (outcome.value?.result === 'win' && outcome.value.prizetype === 3) {
    window.location.assign('/user/?active=3&tab=coins')
    return
  }
  emit('close')
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) requestClose()
}

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      returnFocusTo = document.activeElement as HTMLElement | null
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', onKeydown)
      await nextTick()
      dialog.value?.focus()
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKeydown)
      returnFocusTo?.focus()
    }
  }
)

onBeforeUnmount(() => {
  window.clearTimeout(resultTimer)
  document.body.style.overflow = ''
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="sw-fade">
      <div v-if="open" class="sw-overlay" @click.self="requestClose">
        <div
          ref="dialog"
          class="sw-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sw-title"
          tabindex="-1"
        >
          <button
            type="button"
            class="sw-close"
            aria-label="Close"
            :disabled="stage === 'spinning' || stage === 'checking'"
            @click="requestClose"
          >
            &times;
          </button>

          <header class="sw-head">
            <h2 id="sw-title" class="sw-title">Spin for a chance at travel perks</h2>
            <p class="sw-lead">{{ loggedIn ? 'Your spin is ready. Enter your details for a chance at travel discounts, points or TTG Coins. Any prize is sent to your email.' : 'Create an account to unlock your spin for travel discounts, points or TTG Coins. Any prize is sent to your account email.' }}</p>
          </header>

          <div class="sw-body">
            <SpinWheel
              ref="wheel"
              class="sw-wheel"
              :class="{ 'is-guest': !loggedIn }"
              :segments="segments"
              :disabled="stage !== 'form' && stage !== 'error'"
              @go="onGo"
              @done="onWheelDone"
            />

            <form v-if="stage === 'form' || stage === 'error' || stage === 'checking'" class="sw-form" :class="{ 'is-guest': !loggedIn }" novalidate @submit.prevent="onGo">
              <div class="sw-field">
                <label for="sw-email">Account email (prize is sent here)</label>
                <input
                  id="sw-email"
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  readonly
                  :placeholder="loggedIn ? '' : 'Log in to use your account email'"
                  @click="!loggedIn && onGo()"
                  :aria-invalid="fieldErrors.email ? 'true' : 'false'"
                  aria-describedby="sw-email-error"
                >
                <p id="sw-email-error" class="sw-error" aria-live="polite">{{ fieldErrors.email }}</p>
              </div>

              <div class="sw-row">
                <div class="sw-field">
                  <label for="sw-firstname">First name</label>
                  <input
                    id="sw-firstname"
                    v-model="form.firstname"
                    autocomplete="given-name"
                    :readonly="!loggedIn"
                    :placeholder="loggedIn ? '' : 'Log in to enter your name'"
                    @click="!loggedIn && onGo()"
                    :aria-invalid="fieldErrors.firstname ? 'true' : 'false'"
                    aria-describedby="sw-firstname-error"
                  >
                  <p id="sw-firstname-error" class="sw-error" aria-live="polite">{{ fieldErrors.firstname }}</p>
                </div>
                <div class="sw-field">
                  <label for="sw-lastname">Last name</label>
                  <input
                    id="sw-lastname"
                    v-model="form.lastname"
                    autocomplete="family-name"
                    :readonly="!loggedIn"
                    :placeholder="loggedIn ? '' : 'Log in to enter your name'"
                    @click="!loggedIn && onGo()"
                    :aria-invalid="fieldErrors.lastname ? 'true' : 'false'"
                    aria-describedby="sw-lastname-error"
                  >
                  <p id="sw-lastname-error" class="sw-error" aria-live="polite">{{ fieldErrors.lastname }}</p>
                </div>
              </div>

              <p v-if="formError" class="sw-error sw-error--banner" role="alert">{{ formError }}</p>
              <p v-if="!loggedIn" class="sw-gate__text">Join TripToGo to unlock your spin and see where it lands.</p>
              <p v-if="guestPrompt" class="sw-error sw-error--banner" role="alert">{{ guestPrompt }}</p>
              <button type="submit" class="sw-submit" :disabled="stage === 'checking'">
                {{ stage === 'checking' ? 'Checking your entry...' : 'Confirm and spin' }}
              </button>
              <button v-if="!loggedIn" type="button" class="sw-login" @click="emit('request-login')">Sign up or log in to spin</button>
              <p class="sw-hint">Share the fun: invite a friend to join and try their own spin.</p>
            </form>
          </div>

          <!-- Reward card: frosted glass, shown 600 ms after the wheel stops. -->
          <Transition name="sw-reward">
            <div v-if="stage === 'result'" class="sw-reward" role="status">
              <div class="sw-reward__card">
                <p class="sw-reward__kicker">Your reward</p>
                <h3 class="sw-reward__title">{{ outcome?.result_title }}</h3>
                <p class="sw-reward__body">{{ outcome?.result_description }}</p>
                <p v-if="outcome?.code" class="sw-reward__code">Code <strong>{{ outcome.code }}</strong></p>
                <button type="button" class="sw-reward__done" @click="onDone">Done</button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sw-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgb(8 24 18 / 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  overflow-y: auto;
}

.sw-dialog {
  position: relative;
  width: min(960px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 40px 40px 36px;
  border: 1px solid rgb(223 186 115 / 0.45);
  border-radius: 20px;
  background: linear-gradient(170deg, #143d30 0%, #0d2c22 100%);
  color: #faf6f0;
  font-family: 'Poppins', system-ui, sans-serif;
  box-shadow: 0 30px 80px rgb(0 0 0 / 0.5);
  outline: none;
}

.sw-close {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 5;
  width: 40px;
  height: 40px;
  border: 1px solid rgb(223 186 115 / 0.4);
  border-radius: 50%;
  background: transparent;
  color: #faf6f0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.sw-close:focus-visible {
  outline: 3px solid #dfba73;
  outline-offset: 2px;
}

.sw-close:disabled {
  opacity: 0.35;
  cursor: default;
}

.sw-head {
  margin-bottom: 20px;
  text-align: center;
}

.sw-title {
  margin: 0 0 10px;
  font-size: clamp(36px, 4.2vw, 50px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.01em;
}

.sw-lead {
  margin: 0 auto;
  max-width: 44ch;
  font-size: 19px;
  line-height: 1.55;
  color: rgb(250 246 240 / 0.92);
}

.sw-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 40px;
}

.sw-wheel {
  justify-self: center;
}

.sw-wheel.is-guest {
  filter: grayscale(0.75);
  opacity: 0.7;
}

.sw-form {
  display: grid;
  gap: 12px;
}

.sw-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
}

.sw-field {
  display: grid;
  gap: 6px;
}

.sw-field label {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #e9cf8e;
}

.sw-field input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 56px;
  padding: 0 16px;
  border: 1px solid rgb(223 186 115 / 0.6);
  border-radius: 10px;
  background: rgb(250 246 240 / 0.08);
  color: #faf6f0;
  font: inherit;
  font-size: 18px;
}

.sw-field input[readonly] {
  cursor: not-allowed;
  opacity: 0.8;
}

.sw-form.is-guest .sw-field input,
.sw-form.is-guest .sw-submit {
  background: #52605a;
  border-color: #89918a;
  color: #f1f2ee;
}

.sw-form.is-guest .sw-submit {
  cursor: pointer;
}

.sw-field input::placeholder {
  color: rgb(250 246 240 / 0.5);
}

.sw-field input:focus-visible {
  outline: 2px solid #dfba73;
  outline-offset: 2px;
}

.sw-field input[aria-invalid='true'] {
  border-color: #f0a090;
}

.sw-error {
  min-height: 1.2em;
  margin: 0;
  font-size: 16px;
  color: #f7b8a8;
}

.sw-error:empty {
  display: none;
}

/* Blocking message (already spun, busy, network): a clear box, not a faint line */
.sw-error--banner {
  min-height: 0;
  padding: 12px 14px;
  border: 1px solid #f0a090;
  border-radius: 10px;
  background: rgb(164 54 43 / 0.22);
  color: #ffd9cf;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}

.sw-gate {
  display: grid;
  gap: 20px;
  justify-items: stretch;
}

.sw-gate__text {
  margin: 0;
  font-size: 18px;
  line-height: 1.55;
  color: rgb(250 246 240 / 0.92);
}

.sw-login {
  padding: 12px 16px;
  border: 1px solid #dfba73;
  border-radius: 999px;
  background: transparent;
  color: #faf6f0;
  font: inherit;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
}

.sw-login:focus-visible {
  outline: 3px solid #faf6f0;
  outline-offset: 3px;
}

.sw-submit {
  height: 60px;
  border: 1px solid #dfba73;
  border-radius: 999px;
  background: linear-gradient(145deg, #f3e2b3, #dfba73 50%, #c9a25c);
  color: #143d30;
  font: inherit;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.2s ease;
}

.sw-submit:active:not(:disabled) {
  transform: scale(0.98);
}

.sw-submit:focus-visible {
  outline: 3px solid #faf6f0;
  outline-offset: 3px;
}

.sw-submit:disabled {
  cursor: progress;
  opacity: 0.7;
}

.sw-hint {
  margin: 0;
  font-size: 17px;
  line-height: 1.5;
  color: rgb(250 246 240 / 0.7);
}

.sw-hint strong {
  color: #dfba73;
  font-weight: 600;
}

/* Reward card: glassmorphism over the dialog, with a champagne border */
.sw-reward {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgb(13 44 34 / 0.55);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
}

.sw-reward__card {
  width: min(440px, 100%);
  padding: 36px 32px;
  border: 1px solid rgb(223 186 115 / 0.6);
  border-radius: 18px;
  background: linear-gradient(160deg, rgb(250 246 240 / 0.18), rgb(250 246 240 / 0.06));
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.25),
    0 20px 50px rgb(0 0 0 / 0.35);
  text-align: center;
}

.sw-reward__kicker {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #dfba73;
}

.sw-reward__title {
  margin: 0 0 12px;
  font-size: 40px;
  font-weight: 600;
  line-height: 1.2;
}

.sw-reward__body {
  margin: 0 0 16px;
  font-size: 19px;
  line-height: 1.55;
  color: rgb(250 246 240 / 0.85);
}

.sw-reward__code {
  margin: 0 0 22px;
  font-size: 20px;
}

.sw-reward__done {
  height: 56px;
  padding: 0 40px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(145deg, #f3e2b3, #dfba73 50%, #c9a25c);
  color: #143d30;
  font: inherit;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.sw-reward__done:active {
  transform: scale(0.98);
}

.sw-reward__done:focus-visible {
  outline: 3px solid #faf6f0;
  outline-offset: 3px;
}

.sw-fade-enter-active,
.sw-fade-leave-active {
  transition: opacity 0.3s ease;
}

.sw-fade-enter-from,
.sw-fade-leave-to {
  opacity: 0;
}

.sw-reward-enter-active {
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
}

.sw-reward-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (max-width: 750px) {
  .sw-dialog {
    padding: 28px 20px 24px;
  }

  .sw-head {
    margin-bottom: 16px;
  }

  .sw-title {
    margin-bottom: 6px;
    font-size: 34px;
    line-height: 1.1;
  }

  .sw-lead {
    font-size: 16px;
    line-height: 1.4;
  }

  .sw-body {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .sw-row {
    grid-template-columns: 1fr;
  }

  .sw-field input {
    height: 52px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sw-fade-enter-active,
  .sw-fade-leave-active,
  .sw-reward-enter-active {
    transition: none;
  }
}
</style>
